
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { logger } from "@/utils/logger";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [rateLimitRemaining, setRateLimitRemaining] = useState<number | null>(null);
  
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    
    // Client-side validation
    if (!email.trim()) {
      setError("Please enter your email address");
      setIsLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    try {
      logger.debug("Sending magic link for seller password reset");
      
      // Send magic link that will automatically log the user in and redirect to profile with reset flag
      const { error: magicLinkError } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `https://godyspo.com/seller/profile?reset=true`,
        }
      });

      if (magicLinkError) {
        logger.error("Magic link error", { error: magicLinkError.message });
        
        // Handle specific error cases
        if (magicLinkError.message.includes("rate limit")) {
          setError("Too many reset attempts. Please wait a few minutes before trying again.");
          setRateLimitRemaining(300); // 5 minutes
        } else if (magicLinkError.message.includes("Invalid email")) {
          setError("Please enter a valid email address");
        } else if (magicLinkError.message.includes("User not found")) {
          // For security, don't reveal if email exists - still show success message
          setIsSubmitted(true);
        } else if (magicLinkError.message.includes("Email not confirmed")) {
          setError("Please verify your email address first. Check your inbox for a verification email.");
        } else {
          setError("Unable to send reset email. Please check your email address and try again.");
        }
        
        if (!magicLinkError.message.includes("User not found")) {
          return;
        }
      }

      setIsSubmitted(true);
      logger.debug("Magic link sent successfully");
      
      toast({
        title: "Reset link sent",
        description: "Please check your email for a login link to reset your password.",
      });

      // Start countdown for rate limiting display
      if (rateLimitRemaining) {
        const countdown = setInterval(() => {
          setRateLimitRemaining(prev => {
            if (prev && prev <= 1) {
              clearInterval(countdown);
              return null;
            }
            return prev ? prev - 1 : null;
          });
        }, 1000);
      }

    } catch (error: any) {
      logger.error("Unexpected error during magic link request", { error: error.message });
      
      // Handle network and unexpected errors
      if (!navigator.onLine) {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
      
      toast({
        title: "Error",
        description: error.message || "Failed to send reset link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  return (
    <Card className="w-full border-border bg-card animate-fade-in">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">Forgot Password</CardTitle>
        <CardDescription className="text-center">
          Enter your email and we'll send you a login link to reset your password
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isSubmitted ? (
          <div className="space-y-4 text-center">
            <div className="bg-green-500/20 text-green-500 p-4 rounded-md flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              <span>Password reset link has been sent to your email.</span>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>Please check your email inbox and click the login link to automatically sign in and reset your password.</p>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Don't see the email?</strong>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Check your spam or junk folder</li>
                    <li>Make sure you entered the correct email address</li>
                    <li>Wait a few minutes - emails can sometimes be delayed</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {rateLimitRemaining && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  Please wait {formatTime(rateLimitRemaining)} before requesting another reset email.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(""); // Clear error when user starts typing
                }}
                required
                className="bg-muted"
                disabled={isLoading || !!rateLimitRemaining}
              />
            </div>
            
            <Button
              type="submit"
              className="w-full bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
              disabled={isLoading || !!rateLimitRemaining || !email.trim()}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  Sending Login Link...
                </div>
              ) : (
                "Send Login Link"
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              <p>The login link will automatically sign you in and allow you to change your password.</p>
            </div>
          </form>
        )}
      </CardContent>
      <CardFooter className="flex justify-center">
        <Link
          to="/seller/login"
          className="text-sm text-godyspo-blue hover:text-godyspo-blue/90 underline-offset-4 hover:underline"
        >
          Back to Sign In
        </Link>
      </CardFooter>
    </Card>
  );
};

export default ForgotPasswordPage;
